import React, { useState, useEffect } from "react";
import { getDatabase, ref, get, set } from "firebase/database";
import { app } from "./firebase/config";
// Removed useNavigate import as it's not needed
import "./AdminDashboard.css";
import AdminPostManagement from "./AdminPostManagement";
import AdminUserManagement from "./AdminUserManagement";
import AdminErrorManagement from "./AdminErrorManagement";
import AdminTransactionManagement from "./AdminTransactionManagement";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("adminAuthenticated") === "true";
  });
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [activeTab, setActiveTab] = useState("dashboard");
  // Removed users and transactions arrays to save memory
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalTransactions: 0,
    totalRevenue: 0,
  });
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(() => {
    // Check if there's a saved preference in localStorage
    const savedPreference = localStorage.getItem("sidebarCollapsed");
    return savedPreference ? JSON.parse(savedPreference) : false;
  });

  // Xử lý thay đổi tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Đóng menu trên mobile khi chọn tab
    if (window.innerWidth <= 768) {
      setMenuCollapsed(true);
    }
  };

  // Xử lý toggle menu
  const toggleMenu = () => {
    const newState = !menuCollapsed;
    setMenuCollapsed(newState);
    // Save preference to localStorage
    localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
  };

  // Định dạng số tiền
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(amount);
  };

  // Removed formatDate function as it's no longer needed

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (
      password ===
      (process.env.REACT_APP_ADMIN_PASSWORD || process.env.ADMIN_PASSWORD)
    ) {
      localStorage.setItem("adminAuthenticated", "true");
      setIsAuthenticated(true);
    } else {
      setErrorMsg("Mật khẩu không đúng");
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return; // Không fetch dữ liệu nếu chưa xác thực

    // Lấy trạng thái maintenance mode
    const fetchMaintenanceMode = async () => {
      try {
        const db = getDatabase(app);
        const maintenanceModeRef = ref(db, "system/maintenanceMode");
        const snapshot = await get(maintenanceModeRef);
        if (snapshot.exists()) {
          setMaintenanceMode(snapshot.val());
        }
      } catch (error) {
        console.error("Error fetching maintenance mode:", error);
      }
    };

    fetchMaintenanceMode();

    const fetchData = async () => {
      setLoading(true);
      setError(null); // Reset error state
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/stats-summary`);
        if (response.data?.success) {
          const {
            totalUsers = 0,
            totalTransactions = 0,
            totalRevenue = 0,
          } = response.data.data || {};

          setStats({
            totalUsers,
            activeUsers: totalUsers, // No per-user status needed here
            totalTransactions,
            totalRevenue,
          });
        } else {
          throw new Error(response.data?.message || "Failed to load stats");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Có lỗi khi tải dữ liệu. Vui lòng thử lại sau.");
        // Reset stats on error
        setStats({
          totalUsers: 0,
          activeUsers: 0,
          totalTransactions: 0,
          totalRevenue: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    // Thêm event listener cho window resize để chỉ xử lý mobile view
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        // Chỉ tự động collapse menu khi chuyển sang mobile view
        setMenuCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div
        className="admin-login"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <form
          onSubmit={handlePasswordSubmit}
          style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <h2>Vui lòng nhập mật khẩu Admin</h2>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mật khẩu"
            style={{ padding: "0.5rem", fontSize: "1rem" }}
          />
          {errorMsg && <p style={{ color: "red" }}>{errorMsg}</p>}
          <button type="submit" style={{ padding: "0.5rem", fontSize: "1rem" }}>
            Đăng nhập
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-dashboard flex">
        <div className="admin-sidebar">
          <div className="sidebar-header">
            <h3 className="text-xl font-bold">Admin Dashboard</h3>
          </div>
        </div>
        <div className="main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="mt-4 text-gray-600">Đang tải dữ liệu...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard flex">
        <div className="admin-sidebar">
          <div className="sidebar-header">
            <h3 className="text-xl font-bold">Admin Dashboard</h3>
          </div>
        </div>
        <div className="main-content">
          <div
            className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
            role="alert"
          >
            <strong className="font-bold">Lỗi! </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`admin-dashboard ${menuCollapsed ? "sidebar-collapsed" : ""} ${
        window.innerWidth <= 768 && menuCollapsed ? "mobile-menu-collapsed" : ""
      }`}
    >
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header flex justify-between items-center">
          <h3 className="text-xl font-bold">Admin Dashboard</h3>
          <button
            className="menu-toggle"
            onClick={toggleMenu}
            aria-label="Toggle menu"
            title={menuCollapsed ? "Expand menu" : "Collapse menu"}
          >
            {menuCollapsed ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 5l7 7-7 7M5 5l7 7-7 7"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            )}
          </button>
        </div>
        <div className="mt-4">
          <div
            className={`sidebar-item sidebar-item-tooltip ${
              activeTab === "dashboard" ? "active" : ""
            }`}
            onClick={() => handleTabChange("dashboard")}
            data-tooltip="Dashboard"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
            </svg>
            Dashboard
          </div>
          <div
            className={`sidebar-item sidebar-item-tooltip ${
              activeTab === "users" ? "active" : ""
            }`}
            onClick={() => handleTabChange("users")}
            data-tooltip="Người dùng"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            Người dùng
          </div>
          <div
            className={`sidebar-item sidebar-item-tooltip ${
              activeTab === "posts" ? "active" : ""
            }`}
            onClick={() => handleTabChange("posts")}
            data-tooltip="Bài viết"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4V5h12v10z"
                clipRule="evenodd"
              />
              <path d="M7 7h6v2H7V7zm0 4h6v2H7v-2z" />
            </svg>
            Bài viết
          </div>
          <div
            className={`sidebar-item sidebar-item-tooltip ${
              activeTab === "transactions" ? "active" : ""
            }`}
            onClick={() => handleTabChange("transactions")}
            data-tooltip="Giao dịch"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
              <path
                fillRule="evenodd"
                d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                clipRule="evenodd"
              />
            </svg>
            Giao dịch
          </div>
          {/* <div
            className={`sidebar-item sidebar-item-tooltip ${
              activeTab === "errors" ? "active" : ""
            }`}
            onClick={() => handleTabChange("errors")}
            data-tooltip="Lỗi hệ thống"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            Lỗi hệ thống
          </div> */}
          <div
            className={`sidebar-item sidebar-item-tooltip ${
              activeTab === "settings" ? "active" : ""
            }`}
            onClick={() => handleTabChange("settings")}
            data-tooltip="Cài đặt"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                clipRule="evenodd"
              />
            </svg>
            Cài đặt
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {activeTab === "dashboard" && (
          <>
            <div className="content-header">
              <h2 className="text-2xl font-semibold text-gray-800">
                Tổng quan
              </h2>
            </div>

            {/* Statistics */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-card-header">
                  <p className="stat-card-title">Tổng số người dùng</p>
                  <p className="stat-card-value">{stats.totalUsers}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header">
                  <p className="stat-card-title">Người dùng hoạt động</p>
                  <p className="stat-card-value">{stats.activeUsers}</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-card-header">
                  <p className="stat-card-title">Tổng số giao dịch</p>
                  <p className="stat-card-value">{stats.totalTransactions}</p>
                </div>
              </div>
              <div className="stat-card error-card">
                <div className="stat-card-header">
                  <p className="stat-card-title">Tổng doanh thu</p>
                  <p className="stat-card-value">
                    {formatCurrency(stats.totalRevenue)}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="data-table">
              <div className="table-header">
                <h3 className="table-title">Thao tác nhanh</h3>
              </div>
              <div className="table-content">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
                  <button
                    className="btn btn-primary flex flex-col items-center p-4 rounded-lg hover:shadow-md transition-shadow"
                    onClick={() => handleTabChange("users")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 mb-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                    </svg>
                    <span className="text-sm font-medium">
                      Quản lý người dùng
                    </span>
                  </button>

                  <button
                    className="btn btn-primary flex flex-col items-center p-4 rounded-lg hover:shadow-md transition-shadow"
                    onClick={() => handleTabChange("posts")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 mb-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4V5h12v10z"
                        clipRule="evenodd"
                      />
                      <path d="M7 7h6v2H7V7zm0 4h6v2H7v-2z" />
                    </svg>
                    <span className="text-sm font-medium">
                      Quản lý bài viết
                    </span>
                  </button>

                  <button
                    className="btn btn-primary flex flex-col items-center p-4 rounded-lg hover:shadow-md transition-shadow"
                    onClick={() => handleTabChange("transactions")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 mb-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                      <path
                        fillRule="evenodd"
                        d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Quản lý giao dịch
                    </span>
                  </button>

                  <button
                    className="btn btn-primary flex flex-col items-center p-4 rounded-lg hover:shadow-md transition-shadow"
                    onClick={() => handleTabChange("settings")}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 mb-2"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm font-medium">
                      Cài đặt hệ thống
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === "users" && <AdminUserManagement />}

        {activeTab === "posts" && <AdminPostManagement />}

        {activeTab === "errors" && <AdminErrorManagement />}

        {activeTab === "transactions" && <AdminTransactionManagement />}

        {activeTab === "settings" && (
          <>
            <div className="content-header">
              <h2 className="text-2xl font-semibold text-gray-800">
                Cài đặt hệ thống
              </h2>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Chế độ bảo trì</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 mb-2">
                      Khi bật chế độ bảo trì, người dùng chỉ có thể truy cập
                      trang chủ và trang admin. Tất cả các tính năng khác sẽ bị
                      khóa.
                    </p>
                    <p
                      className={`text-sm ${
                        maintenanceMode ? "text-red-600" : "text-green-600"
                      }`}
                    >
                      Trạng thái:{" "}
                      {maintenanceMode
                        ? "Đang bảo trì"
                        : "Hoạt động bình thường"}
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      className={`px-4 py-2 rounded-lg ${
                        maintenanceMode
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-red-600 hover:bg-red-700"
                      } text-white transition-colors`}
                      onClick={async () => {
                        try {
                          const db = getDatabase(app);
                          const newMode = !maintenanceMode;
                          await set(ref(db, "system/maintenanceMode"), newMode);
                          setMaintenanceMode(newMode);
                        } catch (error) {
                          console.error(
                            "Error updating maintenance mode:",
                            error
                          );
                          setError("Có lỗi khi cập nhật trạng thái bảo trì");
                        }
                      }}
                    >
                      {maintenanceMode
                        ? "Tắt chế độ bảo trì"
                        : "Bật chế độ bảo trì"}
                    </button>
                  </div>
                </div>
              </div>
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Cài đặt khác</h3>
                <p className="text-gray-600">
                  Các tính năng khác đang được phát triển.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
